import { CSSProperties } from 'react'
import { RenderElementProps } from 'slate-react'

import { CUSTOM_TAGS_TO_STYLE } from 'core-components/library/richTextEditor/helpers/htmlHelper'
import { BLOCK_FORMATS } from 'core-components/library/richTextEditor/interface/custom-types'
import { IExtendedBlock } from 'core-components/library/richTextEditor/interface/IExtendedBlock'

import { TCell } from '../interface/TCell'

import {
  ActionBox,
  ExpandBlock,
  ExpandBlockTitle,
  Figure,
  IFrame,
  Image,
  Link,
  Ol,
  Separator,
  Table,
  TableCell,
  Video,
} from './elements'

import styles from './Element.module.scss'

interface IElementProps extends RenderElementProps {
  extendedBlocks?: IExtendedBlock[]
}

export const Element = (props: IElementProps) => {
  let style: CSSProperties = {}
  Object.keys(CUSTOM_TAGS_TO_STYLE).forEach((customStyle) => {
    if (props.element[customStyle]) {
      style = { ...style, ...CUSTOM_TAGS_TO_STYLE[customStyle](props.element) }
    }
  })

  if (props.extendedBlocks) {
    const extendedBlock = props.extendedBlocks.find((block) => block.type === props.element.type)
    if (extendedBlock) {
      return extendedBlock.render(props)
    }
  }

  switch (props.element.type) {
    case BLOCK_FORMATS.blockQuote:
      return (
        <blockquote style={style} className={styles.blockquote} {...props.attributes}>
          {props.children}
        </blockquote>
      )
    case BLOCK_FORMATS.table:
      return <Table style={style} className={styles.table} {...props} />

    case BLOCK_FORMATS.tableRow:
      return (
        <tr style={style} {...props.attributes}>
          {props.children}
        </tr>
      )
    case BLOCK_FORMATS.tableCell:
      return (
        <TableCell
          style={style}
          className={styles.td}
          dataKey={(props.element as TCell).key}
          {...props}
        />
      )
    case BLOCK_FORMATS.bulletedList:
      return (
        <ul style={style} className={styles.bulletedList} {...props.attributes}>
          {props.children}
        </ul>
      )
    case BLOCK_FORMATS.headingOne:
      return (
        <h1 style={style} {...props.attributes}>
          {props.children}
        </h1>
      )
    case BLOCK_FORMATS.headingTwo:
      return (
        <h2 style={style} {...props.attributes}>
          {props.children}
        </h2>
      )

    case BLOCK_FORMATS.headingThree:
      return (
        <h3 style={style} {...props.attributes}>
          {props.children}
        </h3>
      )
    case BLOCK_FORMATS.headingFour:
      return (
        <h4 style={style} {...props.attributes}>
          {props.children}
        </h4>
      )
    case BLOCK_FORMATS.headingFive:
      return (
        <h5 style={style} {...props.attributes}>
          {props.children}
        </h5>
      )
    case BLOCK_FORMATS.headingSix:
      return (
        <h6 style={style} {...props.attributes}>
          {props.children}
        </h6>
      )
    case BLOCK_FORMATS.listItem:
      return (
        <li {...props.attributes} style={style}>
          {props.children}
        </li>
      )
    case BLOCK_FORMATS.numberedList:
      return <Ol style={style} {...props} />
    case BLOCK_FORMATS.image:
      return <Image style={style} {...props} />
    case BLOCK_FORMATS.link:
      return <Link style={style} {...props} />
    case BLOCK_FORMATS.pre:
      return (
        <pre style={style} className={styles.pre}>
          {props.children}
        </pre>
      )
    case BLOCK_FORMATS.span:
      return (
        <span className={styles.span} style={{ ...style, display: 'block' }}>
          {props.children}
        </span>
      )
    case BLOCK_FORMATS.code:
      return (
        <code style={style} className={styles.code}>
          {props.children}
        </code>
      )
    case BLOCK_FORMATS.figure:
      return <Figure style={style} {...props} />
    case BLOCK_FORMATS.video:
      return <Video style={style} {...props} />
    case BLOCK_FORMATS.separator:
      return <Separator style={style} {...props} />
    case BLOCK_FORMATS.iframe:
      return <IFrame style={style} {...props} />
    case BLOCK_FORMATS.action:
      return <ActionBox style={style} {...props} />
    case BLOCK_FORMATS.expandBlock:
      return <ExpandBlock style={style} {...props} />
    case BLOCK_FORMATS.expandBlockTitle:
      return <ExpandBlockTitle style={style} {...props} />
    default:
      return (
        <p style={style} {...props.attributes}>
          {props.children}
        </p>
      )
  }
}
